"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type Message } from "@/components/chat";
import { authFetch, type SessionMode } from "@/lib/api";
import { consumeTurnSSE } from "@/lib/sse";
import { b64ToArrayBuffer } from "@/lib/utils";
import { type AudioMode } from "@/lib/ws";
import { useAudioStreamQueue } from "./useAudioStreamQueue";

export function useSSESession(mode: SessionMode, audioMode: AudioMode = "file") {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [ended, setEnded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueue, resetQueue, getStats } = useAudioStreamQueue();

  useEffect(() => {
    if (!sessionId || ended) return;
    const id = setInterval(async () => {
      try {
        const res = await authFetch(`/session/${sessionId}/status`);
        if (res.ok) {
          const data = await res.json();
          if (data.ended) setEnded(true);
        }
      } catch {
        // polling failure — silent, next tick retries
      }
    }, 3000);
    return () => clearInterval(id);
  }, [sessionId, ended]);

  function handleSessionStart(id: string, greeting: string, audioBase64: string | null = null, cacheHit?: string | null) {
    resetQueue();
    setSessionId(id);
    const msg: Message = { role: "assistant", content: greeting };
    if (cacheHit) msg.debug = { cache_hit: cacheHit };
    setMessages([msg]);
    setEnded(false);
    if (audioBase64) enqueue(b64ToArrayBuffer(audioBase64));
  }

  async function handleSend(text: string, overrideSessionId?: string) {
    const sid = overrideSessionId ?? sessionId;
    if (!sid) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
      const sseMode = mode === "text2audio" ? "text2audio" : "text2text";
      const sendAt = performance.now();
      let firstDeltaAt = 0;
      let audioCount = 0;
      let currentTtfb: number | undefined;

      const res = await authFetch(`/session/${sid}/turn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, mode: sseMode, audio_mode: audioMode }),
      });
      if (!res.ok) throw new Error(`对话请求失败：${res.status}`);

      await consumeTurnSSE(res, (event, data) => {
        if (event === "delta") {
          if (!firstDeltaAt) firstDeltaAt = performance.now();
          const { token } = JSON.parse(data);
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            updated[updated.length - 1] = {
              ...last,
              content: last.content + token,
            };
            return updated;
          });
        } else if (event === "audio") {
          audioCount++;
          if (audioCount === 1 && firstDeltaAt) {
            currentTtfb = Math.round(performance.now() - firstDeltaAt);
          }
          const { audio } = JSON.parse(data);
          enqueue(b64ToArrayBuffer(audio));
        } else if (event === "done") {
          const parsed = JSON.parse(data);
          const { ended: turnEnded, reply, ...serverDebug } = parsed;
          setEnded(turnEnded);
          const debug: Record<string, unknown> = {
            ended: turnEnded,
            ...serverDebug,
          };
          if (firstDeltaAt) {
            debug.ttfb_ms = Math.round(firstDeltaAt - sendAt);
          }
          if (audioCount > 0) {
            debug.tts_sentences = audioCount;
            if (currentTtfb !== undefined) debug.client_tts_ttfb_ms = currentTtfb;
            const audioStats = getStats();
            debug.audio_gaps = audioStats.gaps;
            debug.audio_gap_ms = audioStats.totalGapMs;
          }
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: reply, debug };
            return updated;
          });
        }
      });
    } catch (err) {
      resetQueue();
      toast.error(err instanceof Error ? err.message : String(err));
      // Discard uncommitted partial text as well as the empty placeholder.
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setLoading(false);
    }
  }

  return { sessionId, messages, ended, loading, handleSessionStart, handleSend };
}
